import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import styles from './CarrierLayout.module.css';
import { useAuth } from '../contexts/AuthContext';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { getFailoverAwareDatabase } from '../config/firebaseWithFailover';
import { useEnhancedCarrierNotifications } from '../hooks/useEnhancedCarrierNotifications';
import { MigrationDataAccessLayer } from '../services/migrationDataAccessLayer';
import { onboardingService } from '../services/onboardingService';
import { withErrorBoundary } from '../components/ErrorBoundaryProvider';
import Logger from '../services/logger';
import { useLocationContext } from '../contexts/LocationContext';
import { prefetchCarrierChunk } from '../components/lazy/LazyRoutes';
import { usePortalShellGuard } from '../hooks/usePortalShellGuard';
import { isCarrierPortalUser } from '../utils/portalAccessUtils';

const warmCarrier = (key: Parameters<typeof prefetchCarrierChunk>[0]) => () => {
  prefetchCarrierChunk(key);
};

const CarrierLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [companyName, setCompanyName] = useState<string>('Carrier Portal');
  const [onboardingStatus, setOnboardingStatus] = useState<{
    onboardingCompleted: boolean | null;
    checking: boolean;
  }>({
    onboardingCompleted: null,
    checking: true,
  });
  const { user } = useAuth();
  const { isDriver } = useUserPermissions();
  const { location: userLocation, error: locationError, retry } = useLocationContext();
  
  // Initialize migration data access layer
  const migrationDataAccess = new MigrationDataAccessLayer();
  
  // Use our enhanced notification counting hook (only for sidebar dots, not bell)
  const { counts, markPageVisited } = useEnhancedCarrierNotifications();

  // Re-apply viewport on mount so mobile browser recalculates after client-side nav from login (fixes "must shrink then refresh" glitch)
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      const content = 'width=device-width, initial-scale=1';
      meta.setAttribute('content', content);
      requestAnimationFrame(() => {
        window.visualViewport?.dispatchEvent(new Event('resize'));
      });
    }
  }, []);

  const allowedRoutes = ['/carrier/onboarding'];

  usePortalShellGuard('carrier', allowedRoutes);

  // Check onboarding status (passive check, no redirects)
  // Only runs when user changes (initial load, login/logout), not on every route change
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      // Only check if user exists
      if (!user?.uid) {
        setCompanyName('Carrier Portal');
        setOnboardingStatus({
          onboardingCompleted: null,
          checking: false,
        });
        return;
      }

      try {
        setOnboardingStatus(prev => ({ ...prev, checking: true }));

        // Get user profile FIRST to check user type
        const result = await migrationDataAccess.getUserProfile(user.uid);
        if (!result.success || !result.data) {
          setOnboardingStatus({
            onboardingCompleted: null,
            checking: false,
          });
          return;
        }

        const userData = result.data;
        const isCarrierUser = isCarrierPortalUser(userData as Record<string, unknown>);
        const approvalStatus = userData.approvalStatus;

        if (isCarrierUser && userData.companyName) {
          setCompanyName(userData.companyName);
        }

        // If user is not a carrier user, redirect them immediately - brokers/shippers should NEVER be in CarrierLayout
        if (!isCarrierUser) {
          setOnboardingStatus({
            onboardingCompleted: null,
            checking: false,
          });
          return;
        }
        
        // Compute isAllowedRoute for carrier users
        const isAllowedRoute = allowedRoutes.some(route => location.pathname.includes(route));
        
        // Skip check for allowed routes
        if (isAllowedRoute) {
          setOnboardingStatus({
            onboardingCompleted: null,
            checking: false,
          });
          return;
        }
        
        // Only check approval status for carrier users
        if (approvalStatus !== 'approved') {
          setOnboardingStatus({
            onboardingCompleted: null,
            checking: false,
          });
          return;
        }

        // User is approved carrier - check onboarding status
        const onboardingCompleted = await onboardingService.checkOnboardingStatus(user.uid);
        
        setOnboardingStatus({
          onboardingCompleted: onboardingCompleted,
          checking: false,
        });

        // If onboarding not completed and not on onboarding route, redirect to onboarding
        // Re-compute isAllowedRoute here to use current location at redirect time
        const currentIsAllowedRoute = allowedRoutes.some(route => location.pathname.includes(route));
        if (!onboardingCompleted && !currentIsAllowedRoute) {
          navigate('/carrier/onboarding', { replace: true });
        }
      } catch (error) {
        Logger.error('[CarrierLayout] Error checking onboarding status:', error instanceof Error ? error : new Error(String(error)), { error });
        setOnboardingStatus({
          onboardingCompleted: null,
          checking: false,
        });
      }
    };

    checkOnboardingStatus();
    // Only re-run when user or navigate changes, not on route changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  // Notification counting is now handled by useCarrierNotificationCounts hook

  return (
    <div className={styles.layout}>
      {/* Mobile menu button */}
      <button 
        className={styles.mobileMenuButton}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-label="Toggle menu"
      >
        {isMenuOpen ? (
  <svg width="24" height="24" viewBox="0 0 24 24">
    <line x1="18" y1="6" x2="6" y2="18" stroke="white" strokeWidth="2"/>
    <line x1="6" y1="6" x2="18" y2="18" stroke="white" strokeWidth="2"/>
  </svg>
) : (
  <svg width="24" height="24" viewBox="0 0 24 24">
    <line x1="3" y1="12" x2="21" y2="12" stroke="white" strokeWidth="2"/>
    <line x1="3" y1="6" x2="21" y2="6" stroke="white" strokeWidth="2"/>
    <line x1="3" y1="18" x2="21" y2="18" stroke="white" strokeWidth="2"/>
  </svg>
)}
      </button>

      {/* Sidebar overlay for mobile */}
      {isMenuOpen && <div className={styles.sidebarOverlay} onClick={() => setIsMenuOpen(false)} />}
      
      <nav className={`${styles.sidebar} ${isMenuOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.logo}>
          {/* Add your logo here */}
          <h2>{companyName}</h2>
          {isDriver && (
            <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
              Driver Account
            </div>
          )}
        </div>
        <NavLink 
          to="/carrier/home" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onClick={() => setIsMenuOpen(false)}
          onPointerEnter={warmCarrier('home')}
          onTouchStart={warmCarrier('home')}
        >
          Home
        </NavLink>
        <NavLink 
          to="/carrier/available-loads" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onPointerEnter={warmCarrier('available-loads')}
          onTouchStart={warmCarrier('available-loads')}
          onClick={() => {
            setIsMenuOpen(false);
            markPageVisited('availableLoads');
          }}
        >
          {counts.availableLoads > 0 && <span className={styles.notificationDot}></span>}
          Available Loads
        </NavLink>
        <NavLink 
          to="/carrier/my-loads" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onPointerEnter={warmCarrier('my-loads')}
          onTouchStart={warmCarrier('my-loads')}
          onClick={() => {
            setIsMenuOpen(false);
            markPageVisited('myLoads');
            prefetchCarrierChunk('load-details');
          }}
        >
          {counts.myLoads > 0 && <span className={styles.notificationDot}></span>}
          My Loads
        </NavLink>
        <NavLink 
          to="/carrier/profile" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onPointerEnter={warmCarrier('profile')}
          onTouchStart={warmCarrier('profile')}
          onClick={() => setIsMenuOpen(false)}
        >
          My Profile
        </NavLink>
        <NavLink 
          to="/carrier/payments" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onPointerEnter={warmCarrier('payments')}
          onTouchStart={warmCarrier('payments')}
          onClick={() => {
            setIsMenuOpen(false);
            markPageVisited('payments');
          }}
        >
          {counts.payments > 0 && <span className={styles.notificationDot}></span>}
          Payments
        </NavLink>
        <NavLink 
          to="/carrier/partners" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onPointerEnter={warmCarrier('partners')}
          onTouchStart={warmCarrier('partners')}
          onClick={() => {
            setIsMenuOpen(false);
            markPageVisited('partners');
          }}
        >
          {counts.partners > 0 && <span className={styles.notificationDot}></span>}
          Partners
        </NavLink>
        <NavLink 
          to="/carrier/settings" 
          className={({ isActive }) => isActive ? styles.activeLink : styles.link}
          onPointerEnter={warmCarrier('settings')}
          onTouchStart={warmCarrier('settings')}
          onClick={() => setIsMenuOpen(false)}
        >
          Settings
        </NavLink>
      </nav>
      
      <main className={styles.main}>
        {/* Layout header removed as requested. Only page-level header remains. */}
        <div className={styles.pageContent}>
          {/* Loading overlay - only show while checking, but don't block content */}
          {/* Only show on initial load, not on every route change */}
          {onboardingStatus.checking && !allowedRoutes.some(route => location.pathname.includes(route)) && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 100,
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #007bff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ color: '#666' }}>Checking access...</p>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}

          {/* Always render Outlet - like shipper/broker layouts */}
          <Outlet />
        </div>
      </main>


      {/* Location authentication modal - show for all users when location is not available */}
      {!userLocation && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.45)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              maxWidth: 400,
              boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
              textAlign: 'center',
              position: 'relative',
              zIndex: 100000,
            }}
          >
            <h2 style={{ margin: '0 0 16px 0' }}>Location Required</h2>
            <p style={{ marginBottom: 16 }}>
              This app requires your location to function. Please allow location access when prompted.<br/>
              {locationError && <span style={{ color: '#b00', display: 'block', marginTop: 8 }}>{locationError}</span>}
            </p>
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                Logger.debug('[CarrierLayout] Retry button clicked');
                if (retry) {
                  retry();
                } else {
                  Logger.error('[CarrierLayout] Retry function is not available');
                }
              }} 
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              style={{
                background: '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '10px 24px',
                fontSize: 16,
                cursor: 'pointer',
                fontWeight: 600,
                minWidth: 100,
                position: 'relative',
                zIndex: 100001,
              }}
            >
              Retry
            </button>
            <div style={{ marginTop: 16, fontSize: 13, color: '#666' }}>
              If you previously denied location, please enable it in your browser settings and click Retry.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(CarrierLayout, 'CarrierLayout'); 